import { useMemo, useState } from "react";
import { Copy, Loader2, RotateCcw } from "lucide-react";
import {
  ChangesWarning,
  EarlyTerminationBanner,
  formatPercent,
  isCaseModified,
  MetricCard,
  METRIC_TOOLTIPS,
  ResultBadge,
  TH,
  ViewConfigButton,
} from "./shared";
import { CaseDetailModal } from "./CaseDetailModal";
import type {
  BenchmarkCase,
  BenchmarkMetrics,
  CaseRunResult,
  StoredBenchmarkRun,
} from "../types";

interface ResultsViewProps {
  results: CaseRunResult[];
  metrics: BenchmarkMetrics | null;
  cases: BenchmarkCase[];
  onReset: () => void;
  onBackToHistory: () => void;
  onCopyToCustomRun: () => void;
  onViewConversation: (sessionId: string) => void;
  historyRun?: StoredBenchmarkRun | null;
  isLoading?: boolean;
  shouldTerminateEarly?: boolean;
}

export function ResultsView({
  results,
  metrics,
  cases,
  onReset,
  onBackToHistory,
  onCopyToCustomRun,
  onViewConversation,
  historyRun,
  isLoading,
  shouldTerminateEarly,
}: ResultsViewProps) {
  const [modalCaseId, setModalCaseId] = useState<number | null>(null);

  const caseById = useMemo(
    () => new Map(cases.map((c) => [c.id, c])),
    [cases],
  );

  const sortedResults = useMemo(
    () =>
      [...results].sort((a, b) => {
        const aCustom = a.caseId < 0;
        const bCustom = b.caseId < 0;
        if (aCustom !== bCustom) return aCustom ? 1 : -1;
        return aCustom ? b.caseId - a.caseId : a.caseId - b.caseId;
      }),
    [results],
  );

  const resultByCaseId = useMemo(
    () => new Map(results.map((r) => [r.caseId, r])),
    [results],
  );

  const modalCase = modalCaseId !== null ? caseById.get(modalCaseId) : undefined;
  const modalResult = modalCaseId !== null ? resultByCaseId.get(modalCaseId) : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-brand-foreground flex items-center gap-2">
            Evaluation Results
            {cases.some((c) => c.id < 0) && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700">
                Custom
              </span>
            )}
          </h1>
          {historyRun && (
            <p className="text-sm text-brand-muted mt-1">
              {new Date(historyRun.timestamp).toLocaleString()} &middot;{" "}
              {historyRun.maxConcurrent} concurrent &middot; max {historyRun.maxMessages} messages
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(!!historyRun || !cases.some((c) => c.id < 0)) && (
            <button
              onClick={onCopyToCustomRun}
              className="flex items-center gap-2 px-4 py-2 border border-brand-border text-brand-foreground rounded-md hover:bg-brand-primary/5 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy to Custom Run
            </button>
          )}
          <button
            onClick={historyRun ? onBackToHistory : onReset}
            className="flex items-center gap-2 px-4 py-2 border border-brand-border text-brand-foreground rounded-md hover:bg-brand-primary/5 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            {historyRun ? "Back" : "Run Again"}
          </button>
        </div>
      </div>

      {shouldTerminateEarly && <EarlyTerminationBanner variant="past" />}

      {isLoading ? (
        <div className="flex items-center gap-3 mb-6 p-4 bg-brand-card border border-brand-border rounded-lg">
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
          <span className="text-sm text-brand-muted">Loading results from server...</span>
        </div>
      ) : metrics && !shouldTerminateEarly ? (
        <div className="grid grid-cols-6 gap-4 mb-6">
          <MetricCard label="Accuracy" value={formatPercent(metrics.accuracy)} tooltip={METRIC_TOOLTIPS.accuracy} />
          <MetricCard label="Precision" value={formatPercent(metrics.precision)} tooltip={METRIC_TOOLTIPS.precision} />
          <MetricCard label="Recall" value={formatPercent(metrics.recall)} tooltip={METRIC_TOOLTIPS.recall} />
          <MetricCard
            label="Passed"
            value={`${metrics.passedCases}/${metrics.totalCases - metrics.errorCount}`}
          />
          <MetricCard label="Total Cases" value={String(results.length)} />
          <MetricCard label="Errors" value={String(metrics.errorCount)} />
        </div>
      ) : null}

      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-brand-border rounded-lg shadow">
        <table className="min-w-full bg-brand-card">
          <thead className="sticky top-0 z-10">
            <tr>
              <TH>#</TH>
              <TH>Situation</TH>
              <TH>Segment</TH>
              <TH>Expected Suggestion</TH>
              <TH>Actual Suggestion</TH>
              <TH>Total Messages</TH>
              <TH>Result</TH>
              <TH>{""}</TH>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {sortedResults.map((result, index) => {
              const benchmarkCase = caseById.get(result.caseId);
              if (!benchmarkCase) return null;
              return (
                <tr
                  key={result.caseId}
                  onClick={() => onViewConversation(result.sessionId)}
                  className={`cursor-pointer transition-colors ${result.sessionId
                    ? "hover:bg-brand-primary/5"
                    : "opacity-60 cursor-default"
                    }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-brand-foreground">
                    {benchmarkCase.id < 0 ? index + 1 : benchmarkCase.id}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-foreground max-w-[160px] truncate">
                    {benchmarkCase.modifiedCase.situation}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted max-w-[140px] truncate">
                    {benchmarkCase.modifiedCase.segment || "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted max-w-[180px] truncate">
                    {benchmarkCase.modifiedCase.expectedSuggestion || "None"}
                  </td>
                  <td className="px-4 py-3 text-sm text-brand-muted max-w-[180px] truncate">
                    {result.status === "failed"
                      ? "\u2014"
                      : (result.actualSuggestions.length ? result.actualSuggestions.join(", ") : "None")}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-brand-muted">
                    {result.messageCount}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <ResultBadge result={result} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {isCaseModified(benchmarkCase) && <ChangesWarning />}
                      <ViewConfigButton onClick={(e) => { e.stopPropagation(); setModalCaseId(result.caseId); }} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalCase && (
        <CaseDetailModal
          benchmarkCase={modalCase}
          result={modalResult}
          status={modalResult?.status ?? "pending"}
          messageCount={modalResult?.messageCount ?? 0}
          onClose={() => setModalCaseId(null)}
        />
      )}
    </div>
  );
}
